"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { X, Info, Loader2 } from "lucide-react";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import Checkmark from "@/components/icons/Checkmark";
import { ProductVariant } from "@/hooks/useFunnelState";
import { ProductPlanDetails } from "@/types/product";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

interface CoverageValue {
  display: React.ReactNode;
  detailContent?: React.ReactNode;
  isCovered?: boolean;
}

interface TableRow {
  type: "header" | "data";
  labelKey: string;
  tooltipKey?: string;
  values?: ((planDetails: ProductPlanDetails, t: (key: string, options?: any) => string) => CoverageValue)[];
}

interface ProductVariantTableProps {
  productVariants: ProductVariant[];
  selectedProduct: ProductVariant | null;
  setSelectedProduct: (product: ProductVariant) => void;
  isLoadingPrice: boolean;
  coverageSections: TableRow[];
  initialBundlePrices: { S: number; M: number; L: number } | null;
  paymentInterval: 1 | 2 | 4 | 12;
  isFetchingBundlePrices: boolean;
}

const ProductVariantTable: React.FC<ProductVariantTableProps> = ({
  productVariants,
  selectedProduct,
  setSelectedProduct,
  isLoadingPrice,
  coverageSections,
  initialBundlePrices,
  paymentInterval,
  isFetchingBundlePrices,
}) => {
  const { t } = useTranslation();

  const handleProductSelect = (variantId: string) => {
    const selected = productVariants.find(p => p.id === variantId);
    if (selected) {
      setSelectedProduct(selected);
    }
  };

  return (
    <>
      {/* Mobile: tabs layout */}
      <div className="md:hidden">
        <Tabs
          defaultValue={selectedProduct?.id ?? productVariants[0]?.id}
          onValueChange={(val) => handleProductSelect(val)}
          className="w-full"
        >
          <TabsList className="variant-tabs grid grid-cols-3 gap-2 bg-transparent p-0 relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-primary">
            {productVariants.map((variant) => (
              <TabsTrigger
                key={variant.id}
                value={variant.id}
                className={cn(
                  "variant-tab",
                  "relative text-xl font-bold py-4 rounded-lg border-2 transition-colors",
                  "border-border bg-muted/20", // Inactive state
                  // Active state:
                  "data-[state=active]:bg-background",
                  "data-[state=active]:border-primary",
                  "data-[state=active]:border-b-background", // This "erases" the line behind it
                  "data-[state=active]:rounded-b-none",
                  "data-[state=active]:-mb-[2px]" // Pull it down to cover the line
                )}
              >
                {variant.id.toUpperCase()}
                {variant.id === "l" && (
                  <span className="absolute -top-3 right-2 text-xs font-semibold bg-primary text-primary-foreground px-2 py-1 rounded-md">
                    {t("priceCustomization.table.ourRecommendation")}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {productVariants.map((variant, index) => {
            const isSelected = selectedProduct?.id === variant.id;
            return (
              <TabsContent key={variant.id} value={variant.id} className="relative -mt-[2px]">
                <Card
                  className={cn(
                    "overflow-hidden border-2 rounded-t-none",
                    isSelected ? "border-primary shadow-lg" : "border-border",
                    "border-t-0"
                  )}
                >
                  <CardHeader className="relative px-6 pb-4 pt-8 text-center bg-background">
                    {/* Removed the "Our Recommendation" banner for mobile display */}
                    <div>
                      <h4 className="text-2xl font-bold mb-1">{variant.name}</h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        {variant.description}
                      </p>
                      <p className="text-xl font-bold mb-4 whitespace-nowrap">
                        {isFetchingBundlePrices ? (
                          <span className="inline-flex items-center gap-2 text-primary">
                            <Loader2 className="h-5 w-5 animate-spin" />
                            {t("common.loading")}
                          </span>
                        ) : (
                          initialBundlePrices?.[variant.id.toUpperCase() as "S" | "M" | "L"] !== undefined
                            ? `CHF ${(((initialBundlePrices?.[variant.id.toUpperCase() as "S" | "M" | "L"] ?? 0) * paymentInterval) / 12).toFixed(2)} / ${t("common.monthly")}`
                            : t("common.monthly")
                        )}
                      </p>
                    </div>
                    <Button
                      onClick={() => handleProductSelect(variant.id)}
                      className={cn(
                        "w-full",
                        isSelected
                          ? "bg-continue-button text-continue-button-foreground hover:bg-continue-button/90 text-md"
                          : "bg-primary text-primary-foreground hover:bg-primary/90"
                      )}
                      disabled={isLoadingPrice}
                    >
                      {isSelected ? t("priceCustomization.table.selectedPlan") : t("priceCustomization.table.insureNow")}
                    </Button>
                  </CardHeader>

                  <CardContent className="p-0">
                    <div className="w-full bg-background">
                      {coverageSections.map((row, rowIndex) => {
                        if (row.type === "header") {
                          return (
                            <div
                              key={`header-${row.labelKey}`}
                              className={cn(
                                "p-4 text-sm font-semibold uppercase tracking-wider",
                                isSelected ? "bg-primary/10 text-primary" : "bg-muted/20 text-muted-foreground",
                                "border-t border-b",
                                isSelected ? "border-primary/20" : "border-border"
                              )}
                            >
                              {t(row.labelKey)}
                            </div>
                          );
                        }

                        const coverage = row.values?.[index]?.(variant.details, t);
                        const hasDetails = !!coverage?.detailContent;
                        const isBasicCoverageRow = row.labelKey === "priceCustomization.table.basicCoverageAccidentIllness";

                        return (
                          <Dialog key={`${variant.id}-coverage-${rowIndex}`}>
                            <DialogTrigger asChild>
                              <div
                                className={cn(
                                  "border-b p-4 cursor-pointer relative",
                                  isSelected ? "border-primary/20" : "border-border",
                                  !coverage?.isCovered && "bg-muted/20"
                                )}
                                aria-label={t(row.labelKey)}
                              >
                                {row.tooltipKey && (
                                  <div className="absolute top-2 right-2">
                                    <Info className="h-3 w-3 text-muted-foreground text-smaller opacity-70" aria-hidden="true" />
                                  </div>
                                )}
                                {isBasicCoverageRow ? (
                                  <div className="flex flex-col text-sm">
                                    <span className="font-bold">
                                      {t(row.labelKey)}
                                    </span>
                                    {hasDetails && (
                                      <p
                                        className="text-sm text-muted-foreground mt-1"
                                        dangerouslySetInnerHTML={{ __html: coverage?.detailContent ?? "" }}
                                      />
                                    )}
                                  </div>
                                ) : (
                                  <div className="flex items-start gap-2">
                                    {coverage?.isCovered ? (
                                      <Checkmark className="text-primary mt-1" />
                                    ) : (
                                      <X className={cn("h-4 w-4 flex-shrink-0 mt-1", !coverage?.isCovered && "text-muted-foreground")} />
                                    )}
                                    <div className="flex flex-col text-sm">
                                      <span className={cn(!coverage?.isCovered && "text-muted-foreground")}>
                                        {t(row.labelKey)}
                                      </span>
                                      {hasDetails && (
                                        <p className="text-xs text-muted-foreground mt-1">
                                          {coverage?.detailContent}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>{t(row.labelKey)}</DialogTitle>
                                <DialogDescription asChild>
                                  <div
                                    dangerouslySetInnerHTML={{ __html: t(row.tooltipKey as string) }}
                                  />
                                </DialogDescription>
                              </DialogHeader>
                            </DialogContent>
                          </Dialog>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>

      {/* Desktop: original 3-column grid */}
      <div className="hidden md:grid grid-cols-3 gap-4">
        {productVariants.map((variant, index) => {
          const isSelected = selectedProduct?.id === variant.id;
          const isRecommended = variant.id === "l";

          return (
            <Card
              key={variant.id}
              className={cn(
                "flex flex-col overflow-hidden transition-all duration-200",
                isSelected
                  ? "border-primary ring-2 ring-primary shadow-lg"
                  : "border-border hover:border-primary/50",
              )}
            >
              <CardHeader
                className={cn(
                  "relative px-6 pb-4 pt-10 text-center min-h-[240px] flex flex-col justify-between",
                  "bg-background text-foreground"
                )}
              >
                {isRecommended && (
                  <div
                    className={cn(
                      "absolute top-0 left-0 right-0 py-1 text-sm font-semibold",
                      "bg-primary text-primary-foreground"
                    )}
                  >
                    {t("priceCustomization.table.ourRecommendation")}
                  </div>
                )}
                <div>
                  <h4 className="text-2xl font-bold mb-1">{variant.name}</h4>
                  <p className="text-sm text-muted-foreground mb-4">{variant.description}</p>
                  <p className="text-xl font-bold mb-4 whitespace-nowrap">
                    {isFetchingBundlePrices ? (
                      <span className="inline-flex items-center gap-2 text-primary">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        {t("common.loading")}
                      </span>
                    ) : (
                      initialBundlePrices?.[variant.id.toUpperCase() as "S" | "M" | "L"] !== undefined
                        ? `CHF ${(((initialBundlePrices?.[variant.id.toUpperCase() as "S" | "M" | "L"] ?? 0) * paymentInterval) / 12).toFixed(2)} / ${t("common.monthly")}`
                        : t("common.monthly")
                    )}
                  </p>
                </div>
                <Button
                  onClick={() => handleProductSelect(variant.id)}
                  className={cn(
                    "w-full",
                    isSelected
                      ? "bg-continue-button text-continue-button-foreground hover:bg-continue-button/90 text-md"
                      : "bg-primary text-primary-foreground hover:bg-primary/90"
                  )}
                  disabled={isLoadingPrice}
                >
                  {isSelected ? t("priceCustomization.table.selectedPlan") : t("priceCustomization.table.insureNow")}
                </Button>
              </CardHeader>
              <CardContent className="flex-1 p-0">
                <div className={cn(
                  "w-full",
                  "bg-background"
                )}>
                  {coverageSections.map((row, rowIndex) => {
                    if (row.type === "header") {
                      return (
                        <div
                          key={`header-${row.labelKey}`}
                          className={cn(
                            "p-4 text-sm font-semibold uppercase tracking-wider",
                            isSelected ? "bg-primary/10 text-primary" : "bg-muted/20 text-muted-foreground",
                            "border-t border-b",
                            isSelected ? "border-primary/20" : "border-border"
                          )}
                        >
                          {t(row.labelKey)}
                        </div>
                      );
                    }

                    const coverage = row.values?.[index]?.(variant.details, t);
                    const hasDetails = !!coverage?.detailContent;
                    const isBasicCoverageRow = row.labelKey === "priceCustomization.table.basicCoverageAccidentIllness";

                    return (
                      <Dialog key={`${variant.id}-coverage-${rowIndex}`}>
                        <DialogTrigger asChild>
                          <div
                            className={cn(
                              "border-b p-4 cursor-pointer relative",
                              isSelected ? "border-primary/20" : "border-border",
                              !coverage?.isCovered && "bg-muted/20"
                            )}
                            aria-label={t(row.labelKey)}
                          >
                            {row.tooltipKey && (
                              <div className="absolute top-2 right-2">
                                <Info className="h-3 w-3 text-muted-foreground text-smaller opacity-70" aria-hidden="true" />
                              </div>
                            )}
                            {isBasicCoverageRow ? (
                              <div className="flex flex-col text-sm">
                                <span className="font-bold">
                                  {t(row.labelKey)}
                                </span>
                                {hasDetails && (
                                    <p
                                      className="text-sm text-muted-foreground mt-1"
                                      dangerouslySetInnerHTML={{ __html: coverage?.detailContent ?? "" }}
                                    />        
                                )}
                              </div>
                            ) : (
                              <div className="flex items-start gap-2">
                                {coverage?.isCovered ? (
                                  <Checkmark className="text-primary mt-1" />
                                ) : (
                                  <X className={cn("h-4 w-4 flex-shrink-0 mt-1", !coverage?.isCovered && "text-muted-foreground")} />
                                )}
                                <div className="flex flex-col text-sm">
                                  <span className={cn(!coverage?.isCovered && "text-muted-foreground")}>
                                    {t(row.labelKey)}
                                  </span>
                                  {hasDetails && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {coverage?.detailContent}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>{t(row.labelKey)}</DialogTitle>
                            <DialogDescription asChild>
                              <div
                                dangerouslySetInnerHTML={{ __html: t(row.tooltipKey as string) }}
                              />
                            </DialogDescription>
                          </DialogHeader>
                        </DialogContent>
                      </Dialog>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
};

export default ProductVariantTable;